import {
  DashboardState,
  EColorPickerTab,
  EProjectType,
  ESdChangeReason,
  IKuikaStyle,
  IStylingTypography,
  InvisibleStylePart,
  isGradient,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ColorInputHelper } from "../../../../shared/utility/color-input-helper";
import { loadFontsForPreview, loadFontWeightsForPreview } from "../../../../shared/utility/font-preview-loader";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import StyleLabel from "./style-label/style-label";
import { ColorPicker } from "./styling-color-picker";
import { StylingSelector } from "./styling-selector/styling-selector";
import { styleSectionNeedsRender } from "./styling-window-helpers";

export interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleName: string;
  performStyleTagOnClose: any | undefined;
}

export interface ITypographyComponentState {
  isColorPickerActive: boolean;
  deleteArguments: IDeleteArguments;
}

export interface ITypographyComponentProps {
  form: FormInstance<IKuikaStyle>;
  // KOA-15838: true while a style tag is being edited; drives the blue (token)
  // vs orange (locally changed) label colour independently of the form's section
  // id (which a state switch nulls).
  isEditMode?: boolean;
}

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

class TypographyComponent extends PureSdComponent<ITypographyComponentProps & IWithSDProps, ITypographyComponentState> {
  constructor(props: ITypographyComponentProps & IWithSDProps) {
    super(props);
    this.state = {
      isColorPickerActive: false,
      deleteArguments: { isDeleteStyleModalVisible: false, deletingStyleName: "", performStyleTagOnClose: undefined }
    };
  }

  componentDidMount() {
    const fontNames = this.props.sd?.project?.componentSetObj?.fontNames;
    if (fontNames?.length) {
      loadFontsForPreview(fontNames);
    }
  }

  submitForm = (fieldsValue: IKuikaStyle) => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  new = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.text) {
      return;
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingTypographys = {} as IStylingTypography;
    stylingTypographys.typoId = Guid.create().toString();

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;
    stylingTypographys.name = name;
    stylingTypographys.componentType = selectedDIItem?.name;

    stylingTypographys.styleType = "custom";

    const formValues = {
      fontFamily: fieldsValue.text.fontFamily,
      fontWeight: fieldsValue.text.fontWeight,
      fontStyle: fieldsValue.text.fontStyle,
      fontSizeStyle: fieldsValue.text.fontSizeStyle,
      fontSize: fieldsValue.text.fontSize,
      color: fieldsValue.text.color,
      lineHeight: fieldsValue.text.lineHeight,
      letterSpacing: fieldsValue.text.letterSpacing,
      lineHeightStyle: fieldsValue.text.lineHeightStyle,
      letterSpacingStyle: fieldsValue.text.letterSpacingStyle,
      textDecoration: fieldsValue.text.textDecoration,
      textTransform: fieldsValue.text.textTransform,
      dontAffectedMobileSystem: fieldsValue.text.dontAffectedMobileSystem
    };

    stylingTypographys = {
      ...stylingTypographys,
      ...formValues
    };

    if (!project.stylingTypography) {
      project.stylingTypography = [];
    }
    project.stylingTypography.push(stylingTypographys);

    const savedId = stylingTypographys.typoId;

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.text?.typoIds && savedId) {
      updatedFieldsValue.text.typoIds.push(savedId);
      updatedFieldsValue.text.typoId = savedId;
      updatedFieldsValue.text.name = name;
      this.submitForm(updatedFieldsValue);
    }
  };

  edit = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const editDelegate = () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingTypography) {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        const typographyBackup = _.clone(fieldsValue.text) as IStylingTypography;
        typographyBackup.kuikaid = this.props.sd.selectedKuikaIds[0];
        this.props.sd.setCustomTypographyStyleBackup(typographyBackup);
        const stylingTypographyDefList = this.props.sd.project.stylingTypography;
        const tempValue = stylingTypographyDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          this.setTypographyStyle(tempValue[0]);
        }
      }
    };
    if (mode === EditMode.custom) {
      editDelegate();
    } else {
      this.setTypographyCustomStyle();
      editDelegate();
    }
  };

  duplicate = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const duplicateDelegate = async () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingTypography) {
        const stylingTypographyDefList = this.props.sd.project.stylingTypography;
        const tempValue = stylingTypographyDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          const copyValue = _.cloneDeep(tempValue[0]);
          copyValue.typoId = Guid.create().toString();
          copyValue.name = `${label}_copy`;
          copyValue.styleType = "custom";
          delete copyValue.overridden;

          let unique = false;
          while (!unique) {
            var found = false;
            this.props.sd.project.stylingTypography.forEach((stylingTypography) => {
              if (stylingTypography.name === copyValue.name) {
                copyValue.name += "_copy";
                found = true;
              }
            });
            unique = !found;
          }

          const project = _.cloneDeep(this.props.sd.project);
          project.stylingTypography.push(copyValue);
          const savedId = copyValue.typoId;
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.text?.typoIds && savedId) {
            fieldsValue.text.typoIds.push(savedId);
            this.submitForm(fieldsValue);
          }
        }
      }
    };
    if (mode === EditMode.custom) {
      duplicateDelegate();
    } else {
      this.setTypographyCustomStyle();
      duplicateDelegate();
    }
  };

  removeFromComponent = (performTagOnClose: any) => {
    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  deleteFromProjectConfirmation = (label: string | undefined, performTagOnClose: any) => {
    if (label) {
      this.setState({
        deleteArguments: {
          deletingStyleName: label,
          isDeleteStyleModalVisible: true,
          performStyleTagOnClose: performTagOnClose
        }
      });
    }
  };

  isDefaultStying = (styingId: string) => {
    let found = false;
    this.props.sd.defaultStyleProvider?.getDesktopStyles().typography.map((styling) => {
      if (styling.typoId?.toLowerCase() == styingId?.toLowerCase() || styling.name == styingId) found = true;
    });
    return found;
  };

  isCurrentComponentsDefaultStying = (styingId: string) => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());

    return (
      styingId?.toLowerCase() ==
      ScreenDesignHelper.getDefaultStylingForMobile(
        selectedDi!,
        "typo",
        this.props.sd.defaultStyleProvider!
      )?.toLowerCase()
    );
  };

  deleteFromProject = async () => {
    const label = this.state.deleteArguments.deletingStyleName;

    if (!label) return;

    const currentTypoId = this.props.sd.project.stylingTypography.find((item) => item.name === label)?.typoId;

    const found = this.isDefaultStying(currentTypoId!);

    if (!found) {
      const mode: EditMode = this.getCurrentMode();
      const deleteDelegate = async () => {
        if (label && this.props?.sd?.project?.stylingTypography) {
          const project = _.cloneDeep(this.props.sd.project);
          project.stylingTypography = project.stylingTypography.filter((item) => item.name !== label);
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.text?.typoIds) {
            fieldsValue.text.typoIds = fieldsValue.text.typoIds.filter((typoId) => typoId !== currentTypoId);
            this.submitForm(fieldsValue);
          }

          const { performStyleTagOnClose } = this.state.deleteArguments;
          if (performStyleTagOnClose) {
            performStyleTagOnClose();
          }

          this.setState({
            deleteArguments: {
              isDeleteStyleModalVisible: false,
              deletingStyleName: "",
              performStyleTagOnClose: undefined
            }
          });
        }
      };
      if (mode === EditMode.custom) {
        deleteDelegate();
      } else {
        this.setTypographyCustomStyle();
        deleteDelegate();
      }
    } else {
      KMainFunctions.displayWarningNotification(
        "You cannot remove default styling from a project. If you wish you can remove it from this component"
      );
    }
  };

  getCurrentMode = () => {
    let mode: EditMode = EditMode.custom;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.text?.typoId && fieldsValue.text.typoId.length > 0) {
      if (fieldsValue.text.typoId === "-1") {
        mode = EditMode.create;
      } else {
        mode = EditMode.edit;
      }
    }
    return mode;
  };

  cancel = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text) {
      fieldsValue.text.typoId = null;
      fieldsValue.text.name = "";
      this.submitForm(fieldsValue);
    }
    this.setTypographyCustomStyle();
  };

  save = async (name?: string, _mode?: EditMode) => {
    const mode: EditMode = _mode || this.getCurrentMode();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (!fieldsValue.text) {
      return;
    }

    if (name && name.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please write a name.");
      return;
    }

    if (mode === EditMode.create) {
      const isNameExist = this.props.sd.project.stylingTypography
        .map((s) => s.name)
        .includes(name || fieldsValue.text.name);
      if (isNameExist === true) {
        KMainFunctions.displayWarningNotification(
          "There cannot be another registration with the same name. Please write a different name."
        );
        return;
      }
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingTypographys = {} as IStylingTypography;
    if (mode === EditMode.edit) {
      const data = project.stylingTypography;
      const selectedTypography = fieldsValue?.text?.typoId;
      if (selectedTypography && data && fieldsValue.text) {
        stylingTypographys = data.filter((item) => item.typoId === selectedTypography)[0];
      }
    }

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;

    if (!fieldsValue.text?.letterSpacing || (fieldsValue.text?.letterSpacing as any) === "") {
      delete fieldsValue.text.letterSpacing;
    }
    if (!fieldsValue.text?.lineHeight || (fieldsValue.text?.lineHeight as any) === "") {
      delete fieldsValue.text.lineHeight;
    }
    if (!fieldsValue.text?.fontSize || (fieldsValue.text?.fontSize as any) === "") {
      delete fieldsValue.text.fontSize;
    }
    if (!fieldsValue.text?.fontWeight || (fieldsValue.text?.fontWeight as any) === "") {
      delete fieldsValue.text.fontWeight;
    }

    if (mode === EditMode.create) {
      stylingTypographys.typoId = Guid.create().toString();
    }
    stylingTypographys.name = name || fieldsValue.text.name;
    stylingTypographys.componentType = fieldsValue.text.componentType;
    const formStyleValues = {
      fontFamily: fieldsValue.text.fontFamily,
      fontWeight: fieldsValue.text.fontWeight,
      fontStyle: fieldsValue.text.fontStyle,
      fontSizeStyle: fieldsValue.text.fontSizeStyle,
      fontSize: fieldsValue.text.fontSize,
      color: fieldsValue.text.color,
      lineHeight: fieldsValue.text.lineHeight,
      letterSpacing: fieldsValue.text.letterSpacing,
      lineHeightStyle: fieldsValue.text.lineHeightStyle,
      letterSpacingStyle: fieldsValue.text.letterSpacingStyle,
      textDecoration: fieldsValue.text.textDecoration,
      textTransform: fieldsValue.text.textTransform,
      dontAffectedMobileSystem: fieldsValue.text.dontAffectedMobileSystem
    };

    if (stylingTypographys.styleType !== "custom" && mode === EditMode.edit) {
      const overridden = stylingTypographys.overridden || [];

      Object.keys(formStyleValues).forEach((key) => {
        if (!overridden.includes(key)) {
          if (formStyleValues[key] !== stylingTypographys[key]) {
            overridden.push(key);
          }
        }
      });

      stylingTypographys.overridden = overridden;
    } else {
      stylingTypographys.styleType = "custom";
      delete stylingTypographys.overridden;
    }

    stylingTypographys = {
      ...stylingTypographys,
      ...formStyleValues
    };

    if (!project.stylingTypography) {
      project.stylingTypography = [];
    }
    const exsistingPostionStyleIndex = project.stylingTypography.findIndex(
      (p) => p.typoId === stylingTypographys.typoId
    );
    if (exsistingPostionStyleIndex >= 0) {
      project.stylingTypography[exsistingPostionStyleIndex] = stylingTypographys;
    } else {
      project.stylingTypography.push(stylingTypographys);
    }
    if (mode === EditMode.create) {
      const savedId = stylingTypographys.typoId;
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.clearStylingTypography();
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.text?.typoIds && savedId) {
        fieldsValue.text.typoIds.push(savedId);
        this.submitForm(fieldsValue);
        this.setTypographyCustomStyle();
      }
    } else {
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.setTypographyCustomStyle();
    }
  };

  handleSelectedTagRemoved = (label: string) => {
    if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingTypography) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      const stylingTypographyDefList = this.props.sd.project.stylingTypography;
      const tempValue = stylingTypographyDefList.filter((item) => item.name === label || item.typoId === label);
      if (tempValue && tempValue.length > 0 && fieldsValue.text) {
        if (tempValue[0].typoId === fieldsValue.text.typoId) {
          this.setTypographyCustomStyle();
        }
      }
    }
    return true;
  };

  handleTypographyStylingChange = (value: string | number | Array<string | number> | null) => {
    if (!value || !Array.isArray(value) || value.length === 0) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.text) {
      fieldsValue.text.typoIds = value as string[];

      this.props.form.setFieldsValue(fieldsValue);

      this.props.form.submit();
    }
  };

  setTypographyStyle = (val: IStylingTypography) => {
    if (val) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.text) {
        fieldsValue.text.typoId = val.typoId;
        fieldsValue.text.name = val.name;
        fieldsValue.text.componentType = val.componentType;
        fieldsValue.text.fontFamily = val.fontFamily;
        fieldsValue.text.fontWeight = val.fontWeight;
        fieldsValue.text.fontStyle = val.fontStyle;
        fieldsValue.text.fontSizeStyle = val.fontSizeStyle;
        fieldsValue.text.fontSize = val.fontSize;
        fieldsValue.text.color = val.color;
        fieldsValue.text.lineHeight = val.lineHeight;
        fieldsValue.text.letterSpacing = val.letterSpacing;
        fieldsValue.text.lineHeightStyle = val.lineHeightStyle;
        fieldsValue.text.letterSpacingStyle = val.letterSpacingStyle;
        fieldsValue.text.textDecoration = val.textDecoration;
        fieldsValue.text.textTransform = val.textTransform;
        fieldsValue.text.dontAffectedMobileSystem = val.dontAffectedMobileSystem;

        this.submitForm(fieldsValue);
      }
    }
  };

  setTypographyCustomStyle = () => {
    const { backupCustomTypography } = this.props.sd;
    if (backupCustomTypography?.kuikaid === this.props.sd.selectedKuikaIds[0]) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.text && backupCustomTypography) {
        fieldsValue.text.typoId = null;
        fieldsValue.text.name = backupCustomTypography.name;
        fieldsValue.text.componentType = backupCustomTypography.componentType;
        fieldsValue.text.fontFamily = backupCustomTypography.fontFamily;
        fieldsValue.text.fontWeight = backupCustomTypography.fontWeight;
        fieldsValue.text.fontStyle = backupCustomTypography.fontStyle;
        fieldsValue.text.fontSizeStyle = backupCustomTypography.fontSizeStyle;
        fieldsValue.text.fontSize = backupCustomTypography.fontSize;
        fieldsValue.text.color = backupCustomTypography.color;
        fieldsValue.text.lineHeight = backupCustomTypography.lineHeight;
        fieldsValue.text.letterSpacing = backupCustomTypography.letterSpacing;
        fieldsValue.text.lineHeightStyle = backupCustomTypography.lineHeightStyle;
        fieldsValue.text.letterSpacingStyle = backupCustomTypography.letterSpacingStyle;
        fieldsValue.text.textDecoration = backupCustomTypography.textDecoration;
        fieldsValue.text.textTransform = backupCustomTypography.textTransform;
        fieldsValue.text.dontAffectedMobileSystem = backupCustomTypography.dontAffectedMobileSystem;

        this.clearProjectTempStyle();
        this.submitForm(fieldsValue);
      } else {
        this.clearStylingTypography();
      }
    } else {
      this.clearProjectTempStyle();
    }
  };

  clearProjectTempStyle = async () => {
    const { projectTempStyles } = this.props.sd;
    projectTempStyles.tempStylingTypography = undefined;
    this.props.sd.setProjectTempStyles(projectTempStyles);
    this.props.sd.setCustomTypographyStyleBackup(undefined);
  };

  clearStylingTypography = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text) {
      fieldsValue.text.typoId = null;
      fieldsValue.text.name = undefined;
      fieldsValue.text.fontFamily = undefined;
      fieldsValue.text.fontWeight = undefined;
      fieldsValue.text.fontStyle = undefined;
      fieldsValue.text.fontSizeStyle = "px";
      fieldsValue.text.fontSize = undefined;
      fieldsValue.text.color = undefined;
      fieldsValue.text.lineHeight = undefined;
      fieldsValue.text.letterSpacing = undefined;
      fieldsValue.text.lineHeightStyle = undefined;
      fieldsValue.text.letterSpacingStyle = undefined;
      fieldsValue.text.textDecoration = undefined;
      fieldsValue.text.textTransform = undefined;
      fieldsValue.text.dontAffectedMobileSystem = undefined;
    }
    this.submitForm(fieldsValue);
  };

  handleColorClear = () => {
    const values = this.props.form.getFieldValue("text");
    if (values) {
      values.color = undefined;
    }
    this.props.form.setFieldsValue({ text: values });
    this.props.form.submit();
  };

  componentDidUpdate = (prevProps: ITypographyComponentProps & IWithSDProps, prevState: ITypographyComponentState) => {
    const mode: EditMode = this.getCurrentMode();
    if (mode !== EditMode.custom && prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.clearProjectTempStyle();
      this.setTypographyCustomStyle();
    }
    if (prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.closeColorPicker();
    }
    const fontNames = this.props.sd?.project?.componentSetObj?.fontNames;
    if (fontNames !== prevProps.sd?.project?.componentSetObj?.fontNames && fontNames?.length) {
      loadFontsForPreview(fontNames);
    }
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };

  private static readonly FONT_WEIGHT_LABELS: Array<{ value: number; name: string }> = [
    { value: 100, name: "Thin" },
    { value: 200, name: "Extra Light" },
    { value: 300, name: "Light" },
    { value: 400, name: "Regular" },
    { value: 500, name: "Medium" },
    { value: 600, name: "Semi Bold" },
    { value: 700, name: "Bold" },
    { value: 800, name: "Extra Bold" },
    { value: 900, name: "Black" }
  ];

  /**
   * Every weight is written in itself, so the list shows the difference it is naming. Without a
   * font picked the preview falls back to the panel's own face rather than to no preview at all:
   * the list used to draw all nine names at one weight, which is the one thing a weight list
   * cannot do — `fontFamily: undefined` leaves the span inheriting, and React omits the property.
   */
  getFontWeightOptions = () => {
    const fontFamily: string | undefined = this.props.form.getFieldValue(["text", "fontFamily"]);
    if (fontFamily) {
      loadFontWeightsForPreview(fontFamily);
    }
    return TypographyComponent.FONT_WEIGHT_LABELS.map(({ value, name }) => ({
      value,
      label: <span style={{ fontFamily, fontWeight: value }}>{name}</span>,
      searchLabel: name
    }));
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  handleTextColorChange = (color: string) => {
    const values = this.props.form.getFieldValue("text");
    values.color = color;
    this.props.form.setFieldsValue({ text: values });
    this.props.form.submit();
  };

  handleTextColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    ColorInputHelper.handleColorInputChange(e.target.value, this.handleTextColorChange);
  };

  handleOpacityChange = (e) => {
    const values = this.props.form.getFieldValue("text");
    if (!values.color) return;

    values.color = ColorInputHelper.updateColorOpacity(values.color, e.target.value);
    this.props.form.setFieldsValue({ text: values });
    this.props.form.submit();
  };

  getOpacityFromColor = (color: string) => {
    return ColorInputHelper.getOpacityFromColor(color);
  };

  getTypographyInputClassNames = (fieldName: string, baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    const hasValue = this.hasValue(["text", fieldName]);

    return classNames(baseClassName, {
      "typography__input--has-value": mode === EditMode.edit && hasValue,
      "typography__input--changed": mode !== EditMode.edit && hasValue
    });
  };

  resetSectionValues = (fieldPaths: string[]) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    fieldPaths.forEach((path) => {
      if (fieldsValue.text && fieldsValue.text[path]) {
        fieldsValue.text[path] = undefined;
      }
    });
    this.submitForm(fieldsValue);
  };

  render() {
    const friendlyName = this.getFriendlyName();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const mode: EditMode = this.getCurrentMode();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const isReportDesigner: boolean = this.props.sd.dashboardState === DashboardState.reportDesigner;
    let selectedItem = {};
    if (selectedDi) {
      selectedItem = selectedDi?.item;
    }

    const stylingVersionMenu = (label: string, performTagOnClose: any) => {
      const isDefaultStyling = this.isDefaultStying(label);
      return [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => this.edit(label)
        },
        {
          key: "2",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicate(label)
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => this.removeFromComponent(performTagOnClose)
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteFromProjectConfirmation(label, performTagOnClose),
          danger: true,
          hidden: isDefaultStyling
        }
      ];
    };

    const form = (
      <>
        <Form.Item name={["text", "dontAffectedMobileSystem"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "textDecoration"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "textDecoration"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "textTransform"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "fontStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "typoId"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "componentType"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "name"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "fontFamily"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "fontWeight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "fontSize"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "lineHeight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "letterSpacing"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "color"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["text", "dontAffectedMobileSystem"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>

        <StylingSelector
          formItemName={["text", "typoIds"]}
          stylingList={this.props?.sd?.project?.stylingTypography}
          idField="typoId"
          handleSelectedTagRemoved={this.handleSelectedTagRemoved}
          isDefaultStyling={this.isDefaultStying}
          isCurrentComponentDefaultStyling={this.isCurrentComponentsDefaultStying}
          stylingVersionMenu={stylingVersionMenu}
          defaultStylingRegex={/KUIKA-[a-zA-Z]+-DEFAULT-TYPO(#[a-zA-Z]+#)*$/gm}
          new={this.new}
          cancel={this.cancel}
          save={this.save}
          edit={this.edit}
          mode={mode}
          form={this.props.form}
          onChange={this.handleTypographyStylingChange}
          hide
        />

        <div
          style={{
            display: this.state.isColorPickerActive === true ? "block" : "none"
          }}
          className="kuika_colorPicker"
        >
          <Form.Item name={["text", "color"]} valuePropName="color" noStyle>
            <ColorPicker
              onCancel={this.closeColorPicker}
              isColorPickerActive={this.state.isColorPickerActive}
              onChange={this.handleTextColorChange}
              hideHeaderTabs={!this.isWebSurface()}
            />
          </Form.Item>
        </div>
        <div
          className={classNames("kuika-styling-panel__typography-form", {
            "kuika-styling-panel__editing": mode === EditMode.edit
          })}
        >
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["fontFamily"]}
              section="font family"
              hasValue={this.hasValue(["text", "fontFamily"])}
              className={this.getStyleClassNames(["fontFamily"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Text_FontFamily)}
            >
              Font
            </StyleLabel>
            <div className="kuika-styling-panel__input-wrapper">
              <Form.Item name={["text", "fontFamily"]} noStyle>
                <KuikaSelectbox
                  allowClear
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_FontFamily)}
                  options={this.props.sd?.project?.componentSetObj?.fontNames.map((font) => ({
                    value: font,
                    label: <span style={{ fontFamily: font }}>{font}</span>,
                    searchLabel: font
                  }))}
                  size="sm"
                  className="kuika-styling-panel__selectbox"
                  overlayClassName="kuika-styling-panel__selectbox-overlay"
                />
              </Form.Item>
            </div>
          </div>
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["fontWeight"]}
              section="font weight"
              hasValue={this.hasValue(["text", "fontWeight"])}
              className={this.getStyleClassNames(["fontWeight"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Text_FontWeight)}
            >
              Weight
            </StyleLabel>
            <div className="kuika-styling-panel__input-wrapper">
              <Form.Item name={["text", "fontWeight"]} noStyle>
                <KuikaSelectbox
                  allowClear
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_FontWeight)}
                  options={this.getFontWeightOptions()}
                  size="sm"
                  className="kuika-styling-panel__selectbox"
                  overlayClassName="kuika-styling-panel__selectbox-overlay"
                />
              </Form.Item>
            </div>
          </div>

          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["fontSize"]}
              section="font size"
              hasValue={this.hasValue(["text", "fontSize"])}
              className={this.getStyleClassNames(["fontSize"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Text_FontSize)}
            >
              Size
            </StyleLabel>
            <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--font-size">
              <Form.Item name={["text", "fontSize"]} noStyle>
                <KuikaInput
                  precision={2}
                  placeholder="-"
                  size="sm"
                  type="number"
                  className={this.getTypographyInputClassNames("fontSize")}
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_FontSize)}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      e.preventDefault();
                    }
                    if (KMainFunctions.allowOnlyNumbers(e) || e.key === ".") {
                      return true;
                    }
                    e.preventDefault();
                  }}
                  min={0}
                />
              </Form.Item>

              <Form.Item name={["text", "fontSizeStyle"]} noStyle>
                <KuikaSelectbox
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_FontSize) || isReportDesigner}
                  size="sm"
                  defaultValue="px"
                  options={[
                    { value: "px", label: "px" },
                    { value: "pt", label: "pt" },
                    { value: "em", label: "em" },
                    { value: "rem", label: "rem" }
                  ]}
                  suffixIcon={<></>}
                />
              </Form.Item>
            </div>
          </div>
          <div className="kuika-styling-panel__position-size-wrapper">
            <div className="kuika-styling-panel__position-size-wrapper--child">
              <div className="kuika-styling-panel__position-size-wrapper--child--item">
                <StyleLabel
                  fieldPaths={["letterSpacing"]}
                  section="letter spacing"
                  hasValue={this.hasValue(["text", "letterSpacing"])}
                  className={this.getStyleClassNames(["letterSpacing"])}
                  onReset={this.resetSectionValues}
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_LetterSpacing)}
                >
                  Spacing
                </StyleLabel>
                <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--letter-spacing">
                  <Form.Item name={["text", "letterSpacing"]} noStyle>
                    <KuikaInput
                      precision={0}
                      placeholder="Auto"
                      size="sm"
                      type="number"
                      className={this.getTypographyInputClassNames("letterSpacing")}
                      disabled={this.isStyleHidden(InvisibleStylePart.Text_LetterSpacing)}
                      onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                      min={0}
                    />
                  </Form.Item>
                  <Form.Item name={["text", "letterSpacingStyle"]} noStyle>
                    <KuikaSelectbox
                      disabled={this.isStyleHidden(InvisibleStylePart.Text_LetterSpacing) || isReportDesigner}
                      size="sm"
                      defaultValue="px"
                      options={[
                        { value: "px", label: "px" },
                        { value: "pt", label: "pt" },
                        { value: "em", label: "em" },
                        { value: "rem", label: "rem" }
                      ]}
                      suffixIcon={<></>}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="kuika-styling-panel__position-size-wrapper--child--item">
                <StyleLabel
                  fieldPaths={["lineHeight"]}
                  section="line height"
                  hasValue={this.hasValue(["text", "lineHeight"])}
                  className={this.getStyleClassNames(["lineHeight"])}
                  onReset={this.resetSectionValues}
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_LineHeight)}
                >
                  Line Height
                </StyleLabel>
                <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--line-height">
                  <Form.Item name={["text", "lineHeight"]} noStyle>
                    <KuikaInput
                      precision={0}
                      placeholder="Auto"
                      size="sm"
                      type="number"
                      className={this.getTypographyInputClassNames("lineHeight")}
                      disabled={this.isStyleHidden(InvisibleStylePart.Text_LineHeight)}
                      onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                      min={0}
                    />
                  </Form.Item>
                  <Form.Item name={["text", "lineHeightStyle"]} noStyle>
                    <KuikaSelectbox
                      disabled={this.isStyleHidden(InvisibleStylePart.Text_LineHeight) || isReportDesigner}
                      size="sm"
                      defaultValue="px"
                      options={[
                        { value: "px", label: "px" },
                        { value: "pt", label: "pt" },
                        { value: "em", label: "em" },
                        { value: "rem", label: "rem" }
                      ]}
                      suffixIcon={<></>}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["color"]}
              section="text color"
              hasValue={this.hasValue(["text", "color"])}
              className={this.getStyleClassNames(["color"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Text_Color)}
            >
              Color
            </StyleLabel>
            <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
              <Form.Item noStyle shouldUpdate>
                <KuikaInput
                  placeholder={!this.props.form.getFieldValue(["text", "color"]) ? "TRANSPARENT" : ""}
                  value={
                    isGradient(this.props.form.getFieldValue(["text", "color"]))
                      ? "Gradient"
                      : KMainFunctions.convertRgbaToHex(this.props.form.getFieldValue(["text", "color"]))
                  }
                  disabled={this.isStyleHidden(InvisibleStylePart.Text_Color)}
                  className="kuika-styling-panel__color-input"
                  onClick={(e) => this.toggleTextColorPicker(e)}
                  onChange={this.handleTextColorInputChange}
                  leftUnit={
                    this.props.form.getFieldValue(["text", "color"]) &&
                    this.props.form.getFieldValue(["text", "color"]) !== "transparent" ? (
                      <div
                        onClick={(e) => this.handleLeftUnitClick(e)}
                        style={{
                          width: 24,
                          height: 24,
                          background: this.props.form.getFieldValue(["text", "color"])
                        }}
                      ></div>
                    ) : (
                      <img
                        src={"/images/styling-panel-icons/transparent.svg"}
                        alt="transparent"
                        className="kuika-styling-panel__transparent-icon"
                        onClick={(e) => this.handleLeftUnitClick(e)}
                      />
                    )
                  }
                  rightUnit={
                    <div
                      className="kuika-styling-panel__color-input-unit"
                      style={{ display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      {this.props.form.getFieldValue(["text", "color"]) &&
                        this.props.form.getFieldValue(["text", "color"]) !== "transparent" && (
                          <>
                            <KuikaSvg width={16} height={16} name="trash" onClick={this.handleColorClear} pointer />
                            <KuikaSmallText>HEX</KuikaSmallText>
                          </>
                        )}
                    </div>
                  }
                  size="sm"
                />
              </Form.Item>
            </div>
          </div>
          {!this.isStyleHidden(InvisibleStylePart.Text_TextTransform) && (
            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["textTransform"]}
                section="text transform"
                hasValue={this.hasValue(["text", "textTransform"])}
                className={this.getStyleClassNames(["textTransform"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Text_TextTransform)}
              >
                Caps
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper" style={{ gap: 10 }}>
                <KuikaTabs
                  options={[
                    {
                      value: "X",
                      icon: <KuikaSvg name="x" />
                    },
                    {
                      value: "Uppercase",
                      icon: <KuikaSvg name="letter-case-upper" type="styling-panel-icons" />
                    },
                    {
                      value: "Capitalize",
                      icon: <KuikaSvg name="letter-case" type="styling-panel-icons" />
                    },
                    {
                      value: "Lowercase",
                      icon: <KuikaSvg name="letter-case-lower" type="styling-panel-icons" />
                    }
                  ]}
                  value={
                    fieldsValue?.text?.textTransform
                      ? fieldsValue.text.textTransform.charAt(0).toUpperCase() + fieldsValue.text.textTransform.slice(1)
                      : "X"
                  }
                  onChange={this.handleTextTransformCase}
                />
              </div>
            </div>
          )}
          {!this.isStyleHidden(InvisibleStylePart.Text_TextDecoration) && (
            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["textDecoration"]}
                section="text decoration"
                hasValue={this.hasValue(["text", "textDecoration"])}
                className={this.getStyleClassNames(["textDecoration"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Text_TextDecoration)}
              >
                Decoration
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper">
                <KuikaTabs
                  options={[
                    {
                      value: "X",
                      icon: <KuikaSvg name="x" />
                    },
                    {
                      value: "Underline",
                      icon: <KuikaSvg name="underline" type="styling-panel-icons" />
                    },
                    {
                      value: "Line-through",
                      icon: <KuikaSvg name="strikethrough" type="styling-panel-icons" />
                    }
                  ]}
                  value={
                    fieldsValue?.text?.textDecoration
                      ? fieldsValue.text.textDecoration.charAt(0).toUpperCase() +
                        fieldsValue.text.textDecoration.slice(1)
                      : "X"
                  }
                  onChange={this.handleTextDecorationCase}
                />
              </div>
            </div>
          )}

          {!this.isStyleHidden(InvisibleStylePart.Text_FontWeight) && (
            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["fontStyle"]}
                section="font style"
                hasValue={this.hasValue(["text", "fontStyle"])}
                className={this.getStyleClassNames(["fontStyle"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Text_FontWeight)}
              >
                Italic
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper">
                <KuikaTabs
                  options={[
                    {
                      value: "X",
                      icon: <KuikaSvg name="x" />
                    },
                    {
                      value: "Italic",
                      icon: <KuikaSvg name="check" />
                    }
                  ]}
                  value={fieldsValue?.text?.fontStyle === "italic" ? "Italic" : "X"}
                  onChange={this.handleFontStyleCase}
                />
              </div>
            </div>
          )}

          {this.props.sd.project?.projectType === EProjectType.Mobile && (
            <div className="kuika-styling-panel__typography-form__info-box">
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaInfoBox
                      leftUnit={
                        <KuikaCheckbox
                          checked={this.props.form.getFieldValue(["text", "dontAffectedMobileSystem"]) === true}
                          disabled={this.isStyleHidden(InvisibleStylePart.Text_DontAffectedMobileSystem)}
                          onChange={(e) => this.toggledontAffectedMobileSystem(e)}
                          label="Do not get affected from the mobile system font sizes"
                        />
                      }
                      color="white"
                    />
                  );
                }}
              </Form.Item>
            </div>
          )}
        </div>
      </>
    );

    const deleteModal = (
      <>
        <DeleteModal
          itemName={this.state.deleteArguments.deletingStyleName}
          triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
          deleteItem={() => this.deleteFromProject()}
          deleteType={"Style"}
          closeModal={() =>
            this.setState({
              deleteArguments: {
                isDeleteStyleModalVisible: false,
                deletingStyleName: "",
                performStyleTagOnClose: undefined
              }
            })
          }
        />
      </>
    );
    return (
      <>
        {deleteModal}
        {form}
      </>
    );
  }

  // Gradient text relies on background-clip:text, a CSS-only feature. Expose the
  // gradient tab only on web surfaces (Desktop project or a web screen of a
  // mobile project); native targets can't render it, so they stay solid-only.
  // Mirrors the web-only gate in styling-border-component.tsx.
  private isWebSurface = (): boolean =>
    this.props.sd.project?.projectType === EProjectType.Desktop || this.props.sd.getIsWebScreen();

  toggleTextColorPicker = (event?: any) => {
    if (this.isStyleHidden(InvisibleStylePart.Text_Color)) {
      return;
    }
    this.setState({ isColorPickerActive: !this.state.isColorPickerActive });
    if (event) {
      const currentColor: string = this.props.form.getFieldValue(["text", "color"]);
      KMainFunctions.setColorTriggeredPickerPosition(
        event,
        this.isWebSurface() && isGradient(currentColor) ? EColorPickerTab.Gradient : EColorPickerTab.Solid
      );
    }
  };

  handleLeftUnitClick = (event?: any) => {
    const inputElement = event?.target?.closest(".kuika-styling-panel__input-wrapper")?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
    this.toggleTextColorPicker(event);
  };

  toggleFontWeightBold = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text?.fontWeight === 700) {
      fieldsValue.text.fontWeight = undefined;
    } else if (fieldsValue.text) {
      fieldsValue.text.fontWeight = 700;
    }
    this.submitForm(fieldsValue);
  };

  toggleFontStyle = (value: string | undefined) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text?.fontStyle === value && fieldsValue.text) {
      fieldsValue.text.fontStyle = undefined;
    } else if (fieldsValue.text) {
      fieldsValue.text.fontStyle = value;
    }
    this.submitForm(fieldsValue);
  };

  toggleFontStyleItalic = () => {
    this.toggleFontStyle("italic");
  };

  toggleTextDecoration = (value: string | undefined) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text) {
      fieldsValue.text.textDecoration = value;
    }
    this.submitForm(fieldsValue);
  };

  toggledontAffectedMobileSystem = (e: any) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue || !fieldsValue.text) return;
    fieldsValue.text.dontAffectedMobileSystem = e.target.checked;
    this.submitForm(fieldsValue);
  };

  toggleTextTransform = (value: string | undefined) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.text) {
      fieldsValue.text.textTransform = value;
    }
    this.submitForm(fieldsValue);
  };

  handleTextTransformCase = (value: string) => {
    if (value === "X") {
      this.toggleTextTransform(undefined);
    } else {
      this.toggleTextTransform(value.toLowerCase());
    }
  };

  handleTextDecorationCase = (value: string) => {
    if (value === "X") {
      this.toggleTextDecoration(undefined);
    } else {
      this.toggleTextDecoration(value.toLowerCase());
    }
  };

  closeColorPicker = () => {
    this.setState({ isColorPickerActive: false });
  };

  handleFontWeight = (val: number | undefined): string => {
    if (val === 100) {
      return "Thin";
    }
    if (val === 200) {
      return "Extra-Light";
    }
    if (val === 300) {
      return "Light";
    }
    if (val === 400) {
      return "Regular";
    }
    if (val === 500) {
      return "Medium";
    }
    if (val === 600) {
      return "Semi-Bold";
    }
    if (val === 700) {
      return "Bold";
    }
    if (val === 800) {
      return "Extra-Bold";
    }
    if (val === 900) {
      return "Black";
    }
    return "";
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  hasValue = (fieldPath: string[]) => {
    const value = this.props.form.getFieldValue(fieldPath);
    if (value === "transparent") {
      return false;
    }
    return value !== undefined && value !== null && value !== "";
  };

  getStyleClassNames = (fieldPaths: string[], baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    const hasAnyValue = fieldPaths.some((path) => this.hasValue(["text", path]));
    // KOA-15838: editing a style tag means the value IS a token value → blue
    // (`--has-value`), not the orange "changed" dot — even after a state switch
    // nulls the section id (which would flip getCurrentMode to custom).
    const isTokenValue: boolean = this.props.isEditMode === true || mode === EditMode.edit;

    return classNames(baseClassName, {
      "kuika-styling-panel__label--has-value": isTokenValue && hasAnyValue,
      "kuika-styling-panel__label--changed": !isTokenValue && hasAnyValue
    });
  };

  findStartWithText = (arg: string[]) => {
    const joined: string[] = [];
    arg.forEach((value) => {
      if (value.startsWith("text_")) {
        joined.push(value.substring(5));
      }
    });
    return joined;
  };

  handleFontStyleCase = (value: string) => {
    if (value === "X") {
      this.toggleFontStyle(undefined);
    } else {
      this.toggleFontStyle(value.toLowerCase());
    }
  };
}

const typographyComponent = withSD<ITypographyComponentProps>(TypographyComponent);
export { typographyComponent as TypographyComponent };
